/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import moment from 'moment-timezone';
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {FilledSlots} from '~/SignUpSheets/components/FilledSlots/FilledSlots';
import {VolunteerLinkPreview} from '~/SignUpSheets/components/LinkPreview/VolunteerLinkPreview';
import {SectionRow} from '~/SignUpSheets/components/PageSection/PageSection';
import {useAffiliateActions} from '~/SignUpSheets/hooks/useAffiliateActions';
import {useSaveOrUpdateSignups} from '~/SignUpSheets/hooks/useSaveOrUpdateSignups';
import {sortDownPastTimes, useSortTimes} from '~/SignUpSheets/hooks/useSortTimes';
import {
  BLUEPRINT_TYPE_DATE,
  BLUEPRINT_TYPE_DATETIME_WITH_QUANTITY,
  BLUEPRINT_TYPE_ITEMS,
} from '~/SignUpSheets/reducers/blueprint/constants';
import {
  selectBlueprintType,
  selectIsDateTimeBlueprint,
} from '~/SignUpSheets/reducers/blueprint/selectors';
import {SET_SELECTED_SIGNUPS} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectExpired,
  selectIsOrganizer,
  selectIsPreviewMode,
  selectOrganizerName,
  selectSelectedSignups,
  selectSheetIsDraft,
  selectSignupsByUuid,
  sheetIsArchived,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {selectSlotColor, selectSlotLine} from '~/SignUpSheets/reducers/themes/selectors';
import {
  formatDateLong,
  formatDateShort,
  isAllDay,
  signupOptionIsBeforeToday,
  signupOptionIsToday,
  slotElapsed,
} from '~/SignUpSheets/utils/misc';
import {GroupAvailabilityTag} from '~/SignUpSheets/View/routes/VolunteerView/components/GroupAvailabilityTag/GroupAvailabilityTag';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {Accordion} from '~library/molecules/Accordion';
import {CheckmarkCircleIcon} from '~library/svg/CheckmarkCircleIcon';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {sanitizeString} from '~library/utils/sanitize_html';
import {charcoal, cloud, emeraldWebText, fog} from '~libSass/base/_exports.sass';
import {PurchasedController} from './PurchasedController';
import {SignUpButton} from './SignUpButton';
import {SplitViewLeftColumn} from './SplitViewLeftColumn';
import './VolunteerSignUpRow.sass';

const isSlotSelected = (slot, obj) =>
  obj.slot_id === slot.slot_id && obj.signup_option === slot.signup_option;

const ANIMATION_TIME = 350;
export const VolunteerSignUpRow = ({
  rowData = {
    uuid: '0',
    quantity: 0,
    slots: [],
    title: '',
    description: '',
    product_link: '',
    all_day: false,
  },
  openSignupOverlay,
  setIsEditing,
  allExpired,
  canceled,
  split,
  hideForLegacyDateTime,
  signedInId,
  newUserId,
  grouped = false,
  expanded = false,
  onToggle,
}) => {
  const rowRef = useRef(null);
  const shouldScrollOnExpand = useRef(false);
  const [pendingSlot, setPendingSlot] = useState(null);
  const signedInName = useSelector((state) => state.user.userName);
  const signedInEmail = useSelector((state) => state.user.email);
  const {onSave} = useSaveOrUpdateSignups({
    volunteerEmail: signedInEmail,
    volunteerName: signedInName,
    isEditing: true,
    scope: 'view',
  });
  const blueprintType = useSelector(selectBlueprintType);
  const selectedSignups = useSelector(selectSelectedSignups, shallowEqual);
  const signups = useSelector(selectSignupsByUuid(rowData.uuid), shallowEqual);
  const organizerName = useSelector(selectOrganizerName);
  const past = useSelector((s) => selectExpired(s, {uuid: rowData.uuid}));
  const startDate = useSelector(
    (s) => s.sheet.signup_options.find(({uuid: a}) => a === rowData.uuid),
    shallowEqual
  )?.date;
  const isToday = moment(startDate).isSame(moment(), 'day');
  const isDatetimeType = useSelector(selectIsDateTimeBlueprint);
  const dispatch = useDispatch();
  const {requestLogin} = useRequestLogin();
  const {linkPreviews} = useAffiliateActions();
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const isLargishAndUp = useIsLargishAndUp();
  const sheetIsDraft = useSelector(selectSheetIsDraft);

  const [sectionAnimation] = useAutoAnimate({
    duration: ANIMATION_TIME,
    easing: 'ease-in-out',
  });

  useEffect(() => {
    if (rowRef.current && expanded && shouldScrollOnExpand.current) {
      setTimeout(() => {
        rowRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, ANIMATION_TIME);
    }
  }, [expanded]);

  const handleToggle = useCallback(
    (openState) => {
      shouldScrollOnExpand.current = true; // This avoids auto-scrolling into view until the user has expanded it themselves
      onToggle?.(openState);
    },
    [onToggle]
  );

  const rowSignups = useMemo(() => {
    if (isDatetimeType) return signups;

    return signups.sort((a, b) => {
      if (a.name && !b.name) return -1;
      if (b.name && !a.name) return 1;
      return a.id - b.id;
    });
  }, [signups, isDatetimeType]);

  const toggleSelected = useCallback(
    (slot) => {
      const isSelected = selectedSignups.some((obj) => isSlotSelected(slot, obj));
      const selectedArr = isSelected
        ? selectedSignups.filter((s) => s.signup_option !== slot.signup_option)
        : [...selectedSignups, {...slot}];

      dispatch({
        type: SET_SELECTED_SIGNUPS,
        payload: selectedArr,
      });
    },
    [selectedSignups, dispatch]
  );

  const openOverlayWithSlot = useCallback(
    (slot) => {
      const isSelected = selectedSignups.some((obj) => isSlotSelected(slot, obj));
      if (!isSelected) {
        const selectedArr = [...selectedSignups, ...slot.signups];
        dispatch({
          type: SET_SELECTED_SIGNUPS,
          payload: selectedArr,
        });
      }
      openSignupOverlay();
      setIsEditing(true);
    },
    [dispatch, openSignupOverlay, selectedSignups, setIsEditing]
  );

  const handleEditClick = useCallback(
    async (slot) => {
      if (!(await requestLogin())) return;

      if (signedInId === slot.user_id) {
        openOverlayWithSlot(slot);
      } else {
        setPendingSlot(slot);
      }
    },
    [signedInId, requestLogin, openOverlayWithSlot]
  );

  const onDeclarePurchased = useCallback(
    (signupsToMarkPurchased = []) => {
      if (signupsToMarkPurchased.length === 0) return;
      const updatedSignups = signupsToMarkPurchased.map((s) => ({
        ...s,
        purchased: true,
      }));
      onSave(updatedSignups);
    },
    [onSave]
  );

  const onCancelPurchased = useCallback(
    (signupsToMarkNotPurchased = []) => {
      if (signupsToMarkNotPurchased.length === 0) return;
      const updatedSignups = signupsToMarkNotPurchased.map((s) => ({
        ...s,
        purchased: false,
      }));
      onSave(updatedSignups);
    },
    [onSave]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (pendingSlot && signedInId === pendingSlot.user_id) {
      openOverlayWithSlot(pendingSlot);
      setPendingSlot(null);
    }
  }, [pendingSlot, signedInId]);
  const expired = useMemo(() => past || canceled, [past, canceled]);

  const signupOption = useMemo(() => {
    if (isPreviewMode) {
      if (!sheetIsDraft) {
        return {
          ...rowData,
          slots: rowData.slots
            ? rowData.slots.map((slot) => ({
                ...slot,
                start_time: rowData.all_day ? '12:00 AM' : slot.start_time,
                end_time: rowData.all_day ? '11:59 PM' : slot.end_time,
              }))
            : undefined,
        };
      }
      const baseOption = {
        ...rowData,
        quantity: rowData.quantity || 1,
      };

      if (blueprintType === BLUEPRINT_TYPE_ITEMS) {
        return {
          ...baseOption,
          item: rowData.item || '[Sample Item]',
          description: rowData.description || '[Sample Description]',
        };
      }

      if (blueprintType === BLUEPRINT_TYPE_DATE) {
        return {
          ...baseOption,
          date: rowData.date || '[Sample Date]',
          description: rowData.description || '[Sample Day Description]',
        };
      }

      if (blueprintType === BLUEPRINT_TYPE_DATETIME_WITH_QUANTITY) {
        return {
          ...baseOption,
          date: rowData.date || '[Sample Date]',
          description: rowData.description || '[Sample Day Description]',
          slots: rowData.slots.map((slot) => ({
            ...slot,
            start_time: rowData.all_day ? '12:00 AM' : slot.start_time || '[Start Time]',
            end_time: rowData.all_day ? '11:59 PM' : slot.end_time || '[End Time]',
            title: slot.title || '[Sample Slot Title]',
            description: slot.description || '[Sample Slot Description]',
          })),
        };
      }

      return rowData; // return the original object if no blueprintType matches
    }

    return rowData;
  }, [isPreviewMode, blueprintType, rowData, sheetIsDraft]);

  const SlotsView = useMemo(() => {
    const selectedForPendingSignup = selectedSignups.find(
      (obj) => obj.signup_option === signupOption.uuid
    );
    const hasOpenSlots =
      rowSignups.length < (signupOption.quantity || signupOption.slots.length) && !expired;

    const row = {
      ...signupOption,
      slots: signupOption.slots?.map((slot) => ({
        ...slot,
        product_link: {
          ...(slot.product_link ?? {}),
          ...linkPreviews[slot.slot_id],
        },
      })),
      product_link: {
        ...(signupOption.product_link ?? {}),
        ...linkPreviews[signupOption.uuid],
      },
    };

    if (isLargishAndUp && split && !expanded) {
      return <GroupAvailabilityTag signupOption={signupOption} />;
    }

    return expanded || !split || !isLargishAndUp ? (
      <Slots
        split={split}
        hideForLegacyDateTime={hideForLegacyDateTime}
        selectedForPendingSignup={selectedForPendingSignup}
        expired={expired}
        organizerName={organizerName}
        row={row}
        startDate={startDate}
        past={past}
        allExpired={allExpired}
        isToday={isToday}
        toggleSelected={toggleSelected}
        hasOpenSlots={hasOpenSlots}
        rowSignups={rowSignups}
        selected={selectedSignups}
        signedInId={signedInId}
        newUserId={newUserId}
        handleEditClick={handleEditClick}
        onDeclarePurchased={onDeclarePurchased}
        onCancelPurchased={onCancelPurchased}
      />
    ) : null;
  }, [
    split,
    hideForLegacyDateTime,
    expired,
    signupOption,
    isToday,
    startDate,
    past,
    allExpired,
    toggleSelected,
    rowSignups,
    selectedSignups,
    signedInId,
    newUserId,
    handleEditClick,
    organizerName,
    onDeclarePurchased,
    onCancelPurchased,
    expanded,
    isLargishAndUp,
    linkPreviews,
  ]);

  if (split && !isLargishAndUp)
    return (
      <Accordion
        uniqueAccordionBodyWrapperId={signupOption.uuid}
        className={cx('split-page-section__accordion', {preview: isPreviewMode})}
        accordionQaId={`signup-options-row-${rowData.position_index}`}
        label={formatDateLong(signupOption.date) || '[Sample Date]'}
        labelVariant={isLargishAndUp ? 'header5' : 'paragraph1'}
        labelClassName="split-page-section__slot__date large semibold"
        labelColor={expired ? 'pebble' : 'charcoal'}
        subLabel={signupOption.title}
        subLabelVariant="list2"
        labelDescription={
          <div className="split-page-section__slot__label">
            {!expanded && <GroupAvailabilityTag signupOption={signupOption} />}
            {signupOption.description && (
              <Typography variant="paragraph3" className="signup-sheet__rendered-rich-text">
                {sanitizeString(signupOption.description)}
              </Typography>
            )}
          </div>
        }
        labelDecoration={
          <>
            {past && !allExpired && <Pill expired>past</Pill>}
            {isToday && !past && <Pill>today</Pill>}
          </>
        }
        chevron={<ChevronIcon direction={expanded ? 'up' : 'down'} ratio={0.75} />}
        onClose={() => handleToggle?.(!expanded)}
        onOpen={() => handleToggle?.(!expanded)}
        arrowPlacement="left"
        isOpen={expanded}
        ref={rowRef}
      >
        {SlotsView}
      </Accordion>
    );

  return (
    <SectionRow
      split={split}
      expired={expired}
      sectionQaId={`signup-options-row-${signupOption.position_index}`}
      grouped={grouped}
      ref={rowRef}
    >
      {split && (
        <SplitViewLeftColumn
          dateSlot={signupOption}
          isToday={isToday}
          past={past}
          allExpired={allExpired}
          expanded={expanded}
          onToggle={handleToggle}
        />
      )}
      {split ? (
        <div className={cx('split-page-section__slot__times', {expanded})} ref={sectionAnimation}>
          {SlotsView}
        </div>
      ) : (
        SlotsView
      )}
    </SectionRow>
  );
};

const Slots = ({
  split = false,
  hideForLegacyDateTime,
  expired = false,
  startDate,
  organizerName,
  row: {
    uuid,
    quantity,
    date: rowDate,
    item,
    slots: unsortedSlots = [],
    description,
    product_link: productLink,
  },
  past = false,
  allExpired = false,
  isToday,
  toggleSelected,
  selectedForPendingSignup,
  hasOpenSlots,
  rowSignups = [],
  selected: selectedSignups = [],
  signedInId,
  newUserId,
  handleEditClick,
  onDeclarePurchased,
  onCancelPurchased,
}) => {
  const dispatch = useDispatch();
  const isLargishAndUp = useIsLargishAndUp();
  const slotColor = useSelector(selectSlotColor);
  const slotLine = useSelector(selectSlotLine);
  const isOrganizer = useSelector(selectIsOrganizer);
  const isArchived = useSelector(sheetIsArchived);

  const {list: slots} = useSortTimes(unsortedSlots, (a, b) => sortDownPastTimes(a, b, isToday));

  if (split) {
    return slots.map((slot, i) => {
      const descriptionToShow = slot.description;
      const quantityToShow = slot.quantity || quantity;
      const allDay = isAllDay(slot.start_time, slot.end_time);
      const slotProductLink = slot.product_link;
      const filledForThisSlot = useMemo(
        () =>
          rowSignups
            .filter((f) => f.slot_id === slot.slot_id)
            .map((s) => ({...s, start_time: slot.start_time, end_time: slot.end_time})),
        [slot, rowSignups]
      );
      const isLastItem = i === slots.length - 1;

      const selectedForPendingTimeSignup = selectedSignups.find(
        (obj) => obj.slot_id === slot.slot_id
      );

      const hasOpenSlotsForThisTime = hideForLegacyDateTime
        ? filledForThisSlot.length < 1
        : filledForThisSlot.length < (quantityToShow || slots.length) && !expired;

      const groupedSignups = useMemo(() => {
        return filledForThisSlot.reduce((acc, s) => {
          if (!s.user_id) return acc;
          if (s.user_id in acc) {
            acc[s.user_id].signups.push(s);
            if (!acc[s.user_id].comment && s.comment) {
              acc[s.user_id].comment = s.comment;
            }
          } else {
            acc[s.user_id] = {
              ...s,
              signups: [s],
            };
          }
          return acc;
        }, {});
      }, [filledForThisSlot]);

      const yourSignups = useMemo(
        () => groupedSignups[signedInId || newUserId],
        [groupedSignups, signedInId, newUserId]
      );

      const toggleSelectedTimeSlot = useCallback(
        (timeSlot) => {
          const isSelected = selectedSignups.some((obj) => isSlotSelected(timeSlot, obj));
          const selectedArr = isSelected
            ? selectedSignups.filter((s) => s.slot_id !== timeSlot.slot_id)
            : [...selectedSignups, {...timeSlot}];

          dispatch({
            type: SET_SELECTED_SIGNUPS,
            payload: selectedArr,
          });
        },
        [selectedSignups]
      );

      const PurchasedControllerView = useMemo(
        () =>
          yourSignups ? (
            <PurchasedController
              purchased={yourSignups.purchased}
              onDeclarePurchased={() => onDeclarePurchased(yourSignups.signups)}
              onCancel={() => onCancelPurchased(yourSignups.signups)}
              uuid={slot.slot_id}
            />
          ) : null,
        [yourSignups, onDeclarePurchased, onCancelPurchased, slot.slot_id]
      );

      const timeSlotIsPast = useMemo(
        () =>
          signupOptionIsBeforeToday({date: rowDate}) ||
          (signupOptionIsToday({date: rowDate}) && !allDay && slotElapsed(slot)),
        [rowDate, slot, allDay]
      );

      const SlotActions = useMemo(() => {
        const slotSkeleton = {
          signup_option: uuid,
          user_id: '',
          name: '',
          email: '',
          comment: '',
          slot_id: slot.slot_id,
          start_time: slot.start_time,
          end_time: slot.end_time,
        };
        if (expired) return null;
        if (yourSignups) {
          return (
            <div className="volunteer-signup-row__signup-actions">
              {slotProductLink?.url && slotProductLink?.vendor_name && !yourSignups.purchased ? (
                <>
                  <Button
                    variant="secondary"
                    size="small"
                    icon={<EditLineIcon color={charcoal} ratio={0.5} />}
                    onClick={() => handleEditClick(yourSignups)}
                    data-qa-id="edit-signup"
                    aria-label="Edit signup"
                  />
                  <Link
                    as="primary"
                    href={slotProductLink.affiliate_url}
                    variant="small"
                    data-qa-id="purchase"
                    target="_blank"
                  >
                    Purchase
                  </Link>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  data-qa-id="edit-signup"
                  onClick={() => handleEditClick(yourSignups)}
                >
                  Edit signup
                </Button>
              )}
            </div>
          );
        }
        if (hasOpenSlotsForThisTime && !timeSlotIsPast && !isArchived) {
          return (
            <SignUpButton
              toggleSelected={toggleSelectedTimeSlot}
              slot={slotSkeleton}
              isSelected={selectedForPendingTimeSignup}
              expired={expired}
            />
          );
        }
        if (timeSlotIsPast || !hasOpenSlotsForThisTime) {
          return (
            <Typography
              variant="paragraph2"
              semibold
              color="coin"
              className="volunteer-signup-row__available__filled"
              data-qa-id="signup-filled"
              data-qa-value={filledForThisSlot.length}
            >
              {timeSlotIsPast ? 'Signup Passed' : 'All slots filled'}
            </Typography>
          );
        }
        return null;
      }, [
        yourSignups,
        slotProductLink,
        expired,
        hasOpenSlotsForThisTime,
        toggleSelectedTimeSlot,
        selectedForPendingTimeSignup,
        filledForThisSlot?.length,
        handleEditClick,
        slot?.end_time,
        slot?.slot_id,
        slot?.start_time,
        timeSlotIsPast,
        uuid,
      ]);

      return (
        <Fragment key={slot.slot_id}>
          <div
            className={cx('signup-sheet__volunteer-signup-row', {
              expired,
              'signup-sheet__volunteer-signup-row--full': !hasOpenSlotsForThisTime,
            })}
            style={{
              backgroundColor: !hasOpenSlotsForThisTime && !yourSignups ? fog : slotColor,
              borderLeftColor: !hasOpenSlotsForThisTime && !yourSignups ? cloud : slotLine,
            }}
            data-qa-id="signup-count"
            data-qa-value={quantityToShow || slots.length}
          >
            <div className="volunteer-signup-row__header">
              <div
                className={cx('volunteer-signup-row__title', {
                  past: timeSlotIsPast,
                })}
              >
                <SlotHeading
                  split={split}
                  slot={slot}
                  item={item}
                  isToday={isToday}
                  signedUp={yourSignups}
                />
                <div className="volunteer-signup-row__slot-actions__wrapper">
                  {SlotActions}
                  {!(split && hideForLegacyDateTime) &&
                    quantityToShow &&
                    !timeSlotIsPast &&
                    !isArchived &&
                    hasOpenSlotsForThisTime && (
                      <Typography
                        variant={isLargishAndUp ? 'paragraph3' : 'paragraph4'}
                        className="volunteer-signup-row__available__filled"
                        color={hasOpenSlotsForThisTime ? 'charcoal' : 'coin'}
                        semibold
                        data-qa-id="signup-filled"
                        data-qa-value={filledForThisSlot.length}
                      >
                        {filledForThisSlot.length} of {quantityToShow || slots.length} filled
                      </Typography>
                    )}
                </div>
              </div>
              <div className="volunteer-signup-row__available">
                {!(split && hideForLegacyDateTime) && descriptionToShow && (
                  <Typography
                    variant="paragraph3"
                    color={expired ? 'pebble' : 'shadow'}
                    data-qa-id="signup-description"
                    className="signup-sheet__rendered-rich-text"
                  >
                    {sanitizeString(descriptionToShow)}
                  </Typography>
                )}
              </div>
            </div>
            {!expired && (
              <VolunteerLinkPreview
                uuid={slot.slot_id}
                productLink={slotProductLink}
                organizerName={organizerName}
                disabled={!isOrganizer && !yourSignups}
                endAdornment={PurchasedControllerView}
                titleVariant="paragraph3"
              />
            )}
            <FilledSlots slots={filledForThisSlot} signedInId={signedInId} newUserId={newUserId} />
          </div>
          {!isLargishAndUp && !isLastItem && (
            <span className="signup-sheet__volunteer__accordion-spacer" />
          )}
        </Fragment>
      );
    });
  }

  const groupedSignups = useMemo(() => {
    return rowSignups.reduce((acc, slot) => {
      if (!slot.user_id) return acc;
      if (slot.user_id in acc) {
        acc[slot.user_id].signups.push(slot);
        if (!acc[slot.user_id].comment && slot.comment) {
          acc[slot.user_id].comment = slot.comment;
        }
      } else {
        acc[slot.user_id] = {
          ...slot,
          signups: [slot],
        };
      }
      return acc;
    }, {});
  }, [rowSignups]);

  const yourSignups = useMemo(
    () => groupedSignups[signedInId || newUserId],
    [groupedSignups, signedInId, newUserId]
  );

  const PurchasedControllerView = useMemo(
    () =>
      yourSignups ? (
        <PurchasedController
          purchased={yourSignups.purchased}
          onDeclarePurchased={() => onDeclarePurchased(yourSignups.signups)}
          onCancel={() => onCancelPurchased(yourSignups.signups)}
          uuid={uuid}
        />
      ) : null,
    [yourSignups, onDeclarePurchased, onCancelPurchased, uuid]
  );

  const SlotActions = useMemo(() => {
    if (expired) return null;
    if (yourSignups) {
      return (
        <div className="volunteer-signup-row__signup-actions">
          {productLink?.url && productLink?.vendor_name && !yourSignups.purchased ? (
            <>
              <Button
                variant="secondary"
                size="small"
                icon={<EditLineIcon color={charcoal} ratio={0.5} />}
                onClick={() => handleEditClick(yourSignups)}
                data-qa-id="edit-signup"
              />
              <Link
                as="primary"
                href={productLink.affiliate_url}
                variant="small"
                data-qa-id="purchase"
                target="_blank"
              >
                Purchase
              </Link>
            </>
          ) : (
            <Button
              variant="secondary"
              size="small"
              data-qa-id="edit-signup"
              onClick={() => handleEditClick(yourSignups)}
            >
              Edit signup
            </Button>
          )}
        </div>
      );
    }
    const slotSkeleton = {
      signup_option: uuid,
      user_id: '',
      name: '',
      email: '',
      comment: '',
    };
    if (hasOpenSlots && !past && !allExpired && !isArchived) {
      return (
        <SignUpButton
          toggleSelected={toggleSelected}
          slot={slotSkeleton}
          isSelected={selectedForPendingSignup}
          expired={expired}
        />
      );
    }
    return null;
  }, [
    yourSignups,
    productLink,
    expired,
    isArchived,
    hasOpenSlots,
    toggleSelected,
    selectedForPendingSignup,
    allExpired,
    handleEditClick,
    past,
    uuid,
  ]);

  const disabled = past || allExpired || (!hasOpenSlots && !yourSignups);

  return (
    <div
      className={cx('signup-sheet__volunteer-signup-row', {
        expired,
        'signup-sheet__volunteer-signup-row--full': !hasOpenSlots,
      })}
      style={{
        backgroundColor: disabled ? fog : slotColor,
        borderLeftColor: disabled ? cloud : slotLine,
      }}
      data-qa-id="signup-count"
      data-qa-value={quantity || slots.length}
    >
      <div className="volunteer-signup-row__header">
        <div className="volunteer-signup-row__title">
          <SlotHeading
            split={split}
            slots={slots}
            item={item}
            startDate={startDate}
            past={past}
            allExpired={allExpired}
            isToday={isToday}
            signedUp={yourSignups}
          />
          <div className="volunteer-signup-row__slot-actions__wrapper">
            {SlotActions}
            {hasOpenSlots && !isArchived && (
              <Typography
                variant={isLargishAndUp ? 'paragraph3' : 'paragraph4'}
                className="volunteer-signup-row__available__filled"
                color={hasOpenSlots && !past && !allExpired ? 'charcoal' : 'coin'}
                semibold
                data-qa-id="signup-filled"
                data-qa-value={rowSignups.length}
              >
                {rowSignups.length} of {quantity || slots.length} filled
              </Typography>
            )}
            {((!expired && !hasOpenSlots && !yourSignups) || past) && (
              <Typography
                variant="paragraph2"
                semibold
                className="volunteer-signup-row__available__filled"
                color="coin"
                data-qa-id="signup-filled"
                data-qa-value={rowSignups.length}
              >
                {past ? 'Signup Passed' : 'All full'}
              </Typography>
            )}
          </div>
        </div>
        <div className="volunteer-signup-row__available">
          {((split && !hideForLegacyDateTime) || !split) && description && (
            <Typography
              variant={isLargishAndUp ? 'paragraph2' : 'paragraph3'}
              color={expired ? 'pebble' : 'shadow'}
              data-qa-id="signup-description"
              className="signup-sheet__rendered-rich-text"
            >
              {sanitizeString(description)}
            </Typography>
          )}
        </div>
      </div>
      {!expired && (
        <VolunteerLinkPreview
          uuid={uuid}
          productLink={productLink}
          organizerName={organizerName}
          disabled={!isOrganizer && !yourSignups}
          endAdornment={PurchasedControllerView}
          titleVariant={isLargishAndUp ? 'paragraph2' : 'paragraph3'}
        />
      )}
      <FilledSlots slots={rowSignups} signedInId={signedInId} newUserId={newUserId} />
    </div>
  );
};

const SlotTitle = ({
  split,
  slot,
  item,
  startDate,
  past,
  allExpired,
  isToday,
  signedUp,
  isDatetimeType,
}) => {
  const isLargishAndUp = useIsLargishAndUp();

  const SignedUpCheckmark = useMemo(
    () =>
      signedUp ? (
        <CheckmarkCircleIcon
          ratio={0.5}
          color={emeraldWebText}
          className="volunteer-signup-row__checkmark"
        />
      ) : null,
    [signedUp]
  );

  if (split) {
    const allDay = isAllDay(slot?.start_time, slot?.end_time);

    if (allDay && !slot?.title) {
      return null;
    }

    const hasTimeOrTitle = (!allDay && slot?.start_time) || slot?.title;

    if (hasTimeOrTitle) {
      const hasTimeAndTitle = !allDay && slot?.start_time && slot?.title;
      const timeOrTitle =
        allDay && slot.title ? slot.title : `${slot.start_time} - ${slot.end_time}`;

      return (
        <div className="volunteer-signup-row__title-date">
          <Typography
            variant={isLargishAndUp ? 'header5' : 'list2'}
            semibold
            className="volunteer-signup-row__slot-title"
            data-qa-id="signup-title"
            data-qa-value={timeOrTitle}
          >
            {timeOrTitle} {SignedUpCheckmark}
          </Typography>
          {hasTimeAndTitle && (
            <Typography
              variant="list2"
              semibold
              className="volunteer-signup-row__slot-subtitle"
              data-qa-id="signup-subtitle"
              data-qa-value={slot.title}
            >
              {slot.title}
            </Typography>
          )}
        </div>
      );
    }
  }
  if (isDatetimeType) return null;

  if (item) {
    return (
      <Typography
        variant={isLargishAndUp ? 'header5' : 'paragraph1'}
        semibold
        size="large"
        data-qa-id="signup-title"
        data-qa-value={item}
        className="volunteer-signup-row__slot-title"
      >
        {item}
        {SignedUpCheckmark}
      </Typography>
    );
  }

  return (
    <Typography
      variant="header5"
      semibold
      className="volunteer-signup-row__slot-title"
      data-qa-id="signup-title"
      data-qa-value={startDate}
    >
      {formatDateShort(startDate) || '[Sample Date]'}
      {SignedUpCheckmark}
      {past && !allExpired && <Pill expired>past</Pill>}
      {isToday && !past && <Pill>today</Pill>}
    </Typography>
  );
};

const SlotHeading = ({
  split,
  slot,
  item,
  startDate,
  past,
  allExpired,
  isToday,
  signedUp,
  isDatetimeType,
}) => (
  <div className="volunteer-signup-row__slot-heading">
    <SlotTitle
      split={split}
      slot={slot}
      item={item}
      startDate={startDate}
      past={past}
      allExpired={allExpired}
      isToday={isToday}
      signedUp={signedUp}
      isDatetimeType={isDatetimeType}
    />
  </div>
);

const Pill = ({expired, ...props}) => (
  <Typography
    className={cx('page-section__status-pill', {expired})}
    variant="label2"
    as="span"
    inline
  >
    {props.children}
  </Typography>
);
